import React from 'react';
import { Box } from '@mui/material';

const G1 = '#9FE870', G2 = '#163300', G3 = '#4caf50', G4 = '#2e7d32', BG = '#f0faf0';
const R = ({ x, y, w, h, fill, rx = 3, opacity = 1, style }) => <rect x={x} y={y} width={w} height={h} rx={rx} fill={fill} opacity={opacity} style={style} />;
const T = ({ x, y, size = 6, fill = G2, weight = 400, opacity = 1, children }) => <text x={x} y={y} fontSize={size} fill={fill} fontWeight={weight} fontFamily="Inter,sans-serif" opacity={opacity}>{children}</text>;

const Sidebar = ({ active }) => (
  <g>
    <R x={0} y={26} w={50} h={274} fill={G2} />
    <R x={12} y={34} w={26} h={26} fill={G1} rx={6} />
    <T x={18} y={51} size={9} fill={G2} weight={800}>F</T>
    {[
      { icon: '▦', label: 'Dash',    y: 74  },
      { icon: '◈', label: 'Accts',   y: 94  },
      { icon: '⊞', label: 'Journals',y: 114 },
      { icon: '◉', label: 'Invoices',y: 134 },
      { icon: '⊟', label: 'Payments',y: 154 },
      { icon: '≡', label: 'Reports', y: 174 },
    ].map(({ icon, label, y }) => {
      const on = label === active;
      return (
        <g key={label}>
          {on && <R x={0} y={y - 8} w={50} h={20} fill={G1} opacity={0.15} rx={0} />}
          {on && <R x={0} y={y - 8} w={3}  h={20} fill={G1} rx={0} />}
          <T x={15} y={y + 4}  size={8}   fill={on ? G1 : 'white'} opacity={on ? 1 : 0.4}>{icon}</T>
          <T x={8}  y={y + 14} size={4.5} fill={on ? G1 : 'white'} opacity={on ? 1 : 0.3}>{label}</T>
        </g>
      );
    })}
  </g>
);

const Chrome = ({ title }) => (
  <g>
    <R x={0} y={0} w={480} h={26} fill={G2} rx={12} />
    <R x={0} y={14} w={480} h={12} fill={G2} />
    <circle cx={13} cy={13} r={4} fill="#ff5f57" />
    <circle cx={25} cy={13} r={4} fill="#febc2e" />
    <circle cx={37} cy={13} r={4} fill="#28c840" />
    <T x={480/2 - 30} y={17} size={7} fill={G1} weight={700}>{title}</T>
  </g>
);

const AccountingMockup = () => {
  const W = 480, H = 300;
  const rows = [
    { code: '1001', name: 'Cash & Bank',         type: 'Asset',     balance: '$124,500', color: G3 },
    { code: '1100', name: 'Accounts Receivable',  type: 'Asset',     balance: '$38,200',  color: G3 },
    { code: '2001', name: 'Accounts Payable',     type: 'Liability', balance: '$21,800',  color: '#ef4444' },
    { code: '3001', name: 'Owner Equity',         type: 'Equity',    balance: '$95,000',  color: G4 },
    { code: '4001', name: 'Sales Revenue',        type: 'Revenue',   balance: '$84,200',  color: G1 },
    { code: '5001', name: 'Cost of Goods Sold',   type: 'Expense',   balance: '$31,400',  color: '#f59e0b' },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: 520, position: 'relative', filter: 'drop-shadow(0 20px 40px rgba(22,51,0,0.20))', '& svg': { display: 'block', width: '100%', height: 'auto' } }}>
      <style>{`@keyframes afU{from{opacity:0;transform:translateY(5px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg">
        <defs><linearGradient id="ag" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="white" stopOpacity="0.06"/><stop offset="100%" stopColor="white" stopOpacity="0"/></linearGradient></defs>
        <R x={0} y={0} w={W} h={H} fill={BG} rx={12} />
        <Chrome title="Accounting" />
        <Sidebar active="Accts" />

        {/* topbar */}
        <R x={50} y={26} w={W-50} h={22} fill="white" opacity={0.55} />
        <T x={58} y={41} size={7} fill={G2} weight={700}>Chart of Accounts</T>
        <R x={W-82} y={31} w={54} h={12} fill={G1} rx={6} opacity={0.9} />
        <T x={W-78} y={40} size={5.5} fill={G2} weight={700}>+ New Account</T>

        {/* summary cards */}
        {[
          { label: 'Total Assets',      value: '$162.7K', color: G3,        x: 56  },
          { label: 'Total Liabilities', value: '$21.8K',  color: '#ef4444', x: 140 },
          { label: 'Net Equity',        value: '$140.9K', color: G4,        x: 224 },
          { label: 'Revenue MTD',       value: '$84.2K',  color: G1,        x: 308 },
        ].map(({ label, value, color, x }, i) => (
          <g key={label} style={{ animation: `afU 0.5s ${0.1 + i * 0.1}s ease both` }}>
            <R x={x} y={52} w={78} h={38} fill="white" rx={5} />
            <R x={x} y={52} w={3}  h={38} fill={color} rx={2} />
            <T x={x+7} y={64} size={5.5} fill={G2} opacity={0.5}>{label}</T>
            <T x={x+7} y={80} size={9}   fill={G2} weight={700}>{value}</T>
          </g>
        ))}

        {/* table header */}
        <R x={56} y={96} w={W-62} h={13} fill={G2} rx={3} opacity={0.07} />
        <T x={62}  y={106} size={5.5} fill={G2} weight={700} opacity={0.5}>CODE</T>
        <T x={96}  y={106} size={5.5} fill={G2} weight={700} opacity={0.5}>ACCOUNT NAME</T>
        <T x={260} y={106} size={5.5} fill={G2} weight={700} opacity={0.5}>TYPE</T>
        <T x={370} y={106} size={5.5} fill={G2} weight={700} opacity={0.5}>BALANCE</T>

        {rows.map((row, i) => (
          <g key={row.code} style={{ animation: `afU 0.4s ${0.1 + i * 0.08}s ease both` }}>
            <R x={56} y={111+i*26} w={W-62} h={22} fill="white" rx={3} opacity={i%2===0?0.8:0.5} />
            <T x={62}  y={126+i*26} size={6}   fill={G2} weight={600}>{row.code}</T>
            <T x={96}  y={126+i*26} size={6}   fill={G2}>{row.name}</T>
            <R x={255} y={114+i*26} w={46} h={12} fill={row.color} rx={6} opacity={0.12} />
            <T x={260} y={124+i*26} size={5.5} fill={row.color} weight={600}>{row.type}</T>
            <T x={370} y={126+i*26} size={6}   fill={row.color} weight={700}>{row.balance}</T>
          </g>
        ))}

        <R x={0} y={0} w={W} h={H} fill="url(#ag)" rx={12} />
      </svg>
      <Box aria-hidden sx={{ position:'absolute', inset:'-1px', borderRadius:'13px', border:'1px solid rgba(22,51,0,0.10)', pointerEvents:'none' }} />
    </Box>
  );
};

export default AccountingMockup;
